import http, { HttpSuccessOrErrorType, ListResults } from '../http';
import { Vcode, Staff, Condition, ResultSetHeader } from '../../models';
import { AxiosRequestConfig } from 'axios';

const api= {
    /**
     * 获取验证码。
     * @param params 参数
     */
    async staff_vcode(params: Vcode, config?: AxiosRequestConfig): Promise<HttpSuccessOrErrorType<Vcode>> {
        return await http.post('/staff/vcode', { ...params }, config)
    },

    /**
     * 根据Staff._id查找单个Staff资源。
     * @param params 参数
     */
    async get_staff_id(_id?: string, is_deleted?: boolean, config?: AxiosRequestConfig): Promise<HttpSuccessOrErrorType<Staff>> {
        return await http.get(`/staff/_id/${_id}`, {
            params: {
                is_deleted
            },
            ...config
        })
    },

    /**
     * 修改单个Staff资源。
     * @param params 参数
     */
    async patch_staff_id_id(params: Staff, config?: AxiosRequestConfig): Promise<any> {
        const { _id, ...rest } = params
        return await http.patch(`/staff/_id/${_id}/info`, {
            ...rest
        }, config)
    },

    /**
     * 根据条件查找多个Staff资源。
     * @param params 参数
     */
    async get_staff_condition(params?: Staff & Condition, config?: AxiosRequestConfig):
        Promise<HttpSuccessOrErrorType<ListResults<Staff>[]>> {
        return await http.get('/staff/condition', {
            params: {
                ...params
            },
            ...config
        })
    },

    /**
     * 修改负责人信息
     * @param params 参数
     */
    async patch_staff_id_id_responsible(params: NewResponsible, config?: AxiosRequestConfig):
        Promise<HttpSuccessOrErrorType<any>> {
        const { _id, ...rest } = params;
        return await http.patch(`/staff/_id/${_id}/responsible`, {
            ...rest
        }, config)
    },

    /**
     * 根据Staff._id添加头像
     */
    async patch_staff_id_avatar(_id: string, params: any, config?: AxiosRequestConfig): Promise<any> {
        const formData = new FormData();
        formData.append('', params);
        return (
            await http.patch(`/staff/_id/${_id}/avatar`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                ...config
            })
        );
    },

    /**
     * 查询头像
     * @param params 参数
     */
    async get_staff_id_avatar(id?: string, config?: AxiosRequestConfig): Promise<HttpSuccessOrErrorType<any>> {
        return await http.get(`/staff/_id/${id}/avatar`, {
            ...config
        })
    },

    /**
     * 上传菜单商店联系方式二维码（multipart，与头像接口传参方式一致）
     * `PATCH /v1/staff/_id/{_id}/contact-qr`
     */
    async patch_staff_id_contact_qr(_id: string, file: File, config?: AxiosRequestConfig): Promise<any> {
        const formData = new FormData();
        formData.append('', file);
        return await http.patch(`/staff/_id/${_id}/contact-qr`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            ...config,
        });
    },

    /**
     * 查询指定员工的菜单商店联系方式二维码
     * `GET /v1/staff/_id/{_id}/contact-qr`
     */
    async get_staff_id_contact_qr(id: string, config?: AxiosRequestConfig): Promise<HttpSuccessOrErrorType<unknown>> {
        return await http.get(`/staff/_id/${id}/contact-qr`, {
            ...config,
        });
    },

    /**
     * 删除staff
     * @param params 参数
     */
    async delete_staff_id(id: string, config?: AxiosRequestConfig): Promise<HttpSuccessOrErrorType<any>> {
        return await http.delete(`/staff/${id}`, {
            ...config
        })
    },

    /**
     * 删除staff(硬删除)
     * @param params 参数
     */
    async delete_staff_id_hard(id: string, config?: AxiosRequestConfig): Promise<HttpSuccessOrErrorType<any>> {
        return await http.delete(`/staff/${id}/hard`, {
            ...config
        })
    },

    /**
     * 批量解绑
     * @param params 参数
     */
    async patch_staff_ids_responsibles(params: NewResponsible, config?: AxiosRequestConfig):
        Promise<HttpSuccessOrErrorType<any>> {
        return await http.patch(`/staff/_ids/responsibles`, {
            ...params
        }, config)
    },

    /**
     * 获取到集团下的员工，包括否有负责餐厅
     * @param params 参数
     */
    async get_staff_group_belonged_group_responsible(params: Staff & Condition & { belonged: string }, config?: AxiosRequestConfig):
        Promise<HttpSuccessOrErrorType<ListResults<Staff>[]>> {
        // belonged 0 不绑定  1 绑定 2 全部
        const { belonged_group, ...rest } = params
        return await http.get(`/satff/group/${belonged_group}/responsible`, {
            params: {
                ...rest
            },
            ...config
        })
    },

    /**
     * 创建staff
     * @param params 参数
     */
    async create_staff(params: Staff, config?: AxiosRequestConfig): Promise<HttpSuccessOrErrorType<any>> {
        return await http.post(`/staff`, {
            ...params,
            ...config
        })
    },

    /**
     * 批量删除staff
     * @param params 参数
     */
    async delete_staff(ids: Array<string>, config?: AxiosRequestConfig): Promise<HttpSuccessOrErrorType<any>> {
        return await http.delete(`/staff`, {
            data: {
                _id: ids
            },
            ...config
        })
    },

    /**
     * 批量删除staff
     * @param params 参数
     */
    async delete_staff_hard(ids: Array<string>): Promise<HttpSuccessOrErrorType<any>> {
        return await http.delete(`/staff/hard`, {
            data: {
                _id: ids
            }
        })
    },

    /**
     * 修改关联的餐厅信息
     * @param params 参数
     */
    async patch_staff_id_belonged_restaurant(params: Responsible, config?: AxiosRequestConfig):
        Promise<HttpSuccessOrErrorType<any>> {
        const { _id, ...rest } = params;
        return await http.patch(`/staff/${_id}/belonged_restaurant`, {
            ...rest
        }, config)
    },
    /**
     * 修改密码
     * @param params 参数
     */
    async patch_staff_password_current(params: { _id: string, currentPassword: string, newPassword: string }, config?: AxiosRequestConfig): Promise<HttpSuccessOrErrorType<ResultSetHeader>> {
        return await http.patch(`/staff/password/current`, {
            ...params
        }, {
            ...config
        })
    },
    /**
     * 密码初始化
     * @param params 参数
     */
    async post_staff_id_password_initialize(params: { _id: string }, config?: AxiosRequestConfig): Promise<HttpSuccessOrErrorType<ResultSetHeader>> {
        return await http.patch(`/staff/_id/${params._id}/password`, {
            ...params
        }, {
            ...config
        })
    }
}

export default api

export type NewResponsible = {
    staffIds?: Array<string | undefined>,
    _id?: string,
    type?: string,
    option?: string,
    responsible_dealer?: string,
    responsible_restaurant?: string[] | string,
    responsible_group?: string
}

// 负责任类型定义
export type Responsible = {
    _id?: string,
    type?: string,
    option?: string,
    responsible_dealer?: string,
    responsible_restaurant?: string[],
    responsible_group?: string
}

// 负责人
export type Responsibles = {
    staffIds?: Array<string | undefined>,
    type?: string,
    option?: string,
    responsible_dealer?: string,
    responsible_restaurant?: string,
    responsible_group?: string,
}